"use client";

import * as React from "react";
import Link from "next/link";
import {
  Award,
  ChevronRight,
  CreditCard,
  HelpCircle,
  Mail,
  Palette,
  Plus,
  Users,
} from "lucide-react";
import {
  Badge,
} from "@/components/ui/badge";
import {
  Button,
} from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState } from "@/components/state/empty-states";
import {
  Eyebrow,
  Panel,
} from "@/components/layout/app-shell";
import {
  StatTilesSkeleton,
  TableSkeleton,
} from "@/components/state/skeletons";
import {
  StatusBadge,
  type CertStatus,
} from "@/components/certificates/certificates-table";
import {
  cn,
} from "@/lib/utils";

export type Role = "OPERATOR" | "ADMIN" | "USER";

export type Meter = {
  label: string;
  used: number;
  included: number;
  max: number;
  /** e.g. "£1.00 per certificate" — used to build the overage sentence. */
  overageUnitLabel: string;
  unitNoun: string;
};

export type HomeData = {
  tenant: {
    name: string;
    initials: string;
    logoSrc?: string;
    planName: string;
    subscriptionStatus: "active" | "trialing" | "past_due" | "expired";
    renewalDate: string;
  };
  cost: { estimatedLabel: string; breakdownLabel: string };
  meters: { certificates: Meter; seats: Meter };
  recent: {
    id: string;
    name: string;
    competency: string;
    issuedAt: string;
    status: CertStatus;
    emailSent: boolean;
  }[];
  totalCertificates: number;
  attention: {
    id: string;
    kind: "expiring" | "email" | "limit";
    title: string;
    meta: string;
    ctaLabel: string;
    href: string;
  }[];
};

const STATUS_CHIP: Record<HomeData["tenant"]["subscriptionStatus"], string> = {
  active: "border-success-border bg-success text-success-foreground",
  trialing: "border-warning-border bg-warning text-warning-foreground",
  past_due: "border-warning-border bg-warning text-warning-foreground",
  expired: "border-danger-border bg-danger text-danger-foreground",
};

const STATUS_WORD: Record<HomeData["tenant"]["subscriptionStatus"], string> = {
  active: "active",
  trialing: "trial",
  past_due: "payment due",
  expired: "expired",
};

/**
 * Over-allowance is a billing fact, not an error: amber chip + one plain sentence.
 * Never red, never a raw "223/100".
 */
function UsageMeter({ meter }: { meter: Meter }) {
  const over = Math.max(0, meter.used - meter.included);
  // Fill reflects usage, not the size of the included allowance: the bar has to
  // read empty at 0 used and full at the included limit.
  const total = Math.max(meter.used, meter.included, 1);
  const usedPct = (Math.min(meter.used, meter.included) / total) * 100;
  const overPct = (over / total) * 100;

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center gap-2">
        <Eyebrow>{meter.label}</Eyebrow>
        <div className="flex-1" />
        {over > 0 && (
          <Badge
            variant="outline"
            className="rounded-full border-accent-foreground/20 bg-accent px-2.5 py-0.5 text-xs font-medium text-accent-foreground"
          >
            Over allowance
          </Badge>
        )}
      </div>

      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-semibold tabular-nums">
          {meter.used.toLocaleString()}
        </span>
        <span className="text-[13px] text-muted-foreground">
          of {meter.included.toLocaleString()} included · max {meter.max.toLocaleString()}
        </span>
      </div>

      <div
        className="flex h-2 gap-[3px] overflow-hidden rounded-full bg-muted"
        role="img"
        aria-label={`${meter.used} used, ${meter.included} included${over > 0 ? `, ${over} over` : ""}`}
      >
        {/* Segment colours are the semantic ink tones, never surface tokens:
            bg-sidebar and bg-primary matched the track in one theme or another
            and the bar vanished. The 3px gap separates the segments for anyone
            who cannot tell the two hues apart. */}
        <div className="rounded-full bg-success-foreground" style={{ width: `${usedPct}%` }} />
        {over > 0 && (
          <div className="rounded-full bg-danger-foreground" style={{ width: `${overPct}%` }} />
        )}
      </div>

      <p className="text-[13px] text-foreground/80">
        {over > 0
          ? `${over.toLocaleString()} above included — billed ${meter.overageUnitLabel}. See Plan & billing for rates.`
          : `${(meter.included - meter.used).toLocaleString()} ${meter.unitNoun} left this period.`}
      </p>
    </div>
  );
}

function TenantHeader({
  data,
  role,
  onIssue,
}: {
  data: HomeData;
  role: Role;
  onIssue?: () => void;
}) {
  const t = data.tenant;
  return (
    <header className="flex items-start gap-4 max-md:flex-wrap">
      {t.logoSrc ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={t.logoSrc}
          alt=""
          className="size-12 shrink-0 rounded-xl border object-cover"
        />
      ) : (
        <span className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-primary text-base font-semibold text-primary-foreground">
          {t.initials}
        </span>
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <Eyebrow>Overview</Eyebrow>
        <h1 className="text-[30px] font-semibold leading-none tracking-[-0.015em]">
          {t.name}
        </h1>
        <div className="flex flex-wrap items-center gap-2.5">
          <Badge
            variant="outline"
            className={cn("rounded-full px-2.5 py-1 text-xs font-medium", STATUS_CHIP[t.subscriptionStatus])}
          >
            {t.planName} plan · {STATUS_WORD[t.subscriptionStatus]}
          </Badge>
          {t.renewalDate && t.renewalDate !== "—" ? (
            <span className="text-[13px] text-muted-foreground">Renews {t.renewalDate}</span>
          ) : null}
          {role !== "USER" && data.cost.estimatedLabel ? (
            <>
              <span className="h-3.5 w-px bg-border" />
              <span className="text-[13px] text-muted-foreground">
                Est. {data.cost.estimatedLabel} this month
              </span>
            </>
          ) : null}
        </div>
      </div>

      <div className="flex items-center gap-2 pt-[26px] max-md:w-full max-md:pt-0">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="icon" aria-label="About usage" className="size-9">
              <HelpCircle className="size-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-4">
            <div className="flex flex-col gap-2">
              <Eyebrow>Included vs billed</Eyebrow>
              <p className="text-[13px] leading-relaxed text-foreground/80">
                Your plan includes an allowance each period. Going over isn’t a problem —
                the extra is billed per unit and shown on your next invoice.
              </p>
            </div>
          </PopoverContent>
        </Popover>
        <Button variant="outline" asChild>
          <Link href="/verify">Verify a certificate</Link>
        </Button>
        {role !== "USER" && (
          <Button className="gap-2" onClick={onIssue}>
            <Plus className="size-4" />
            Issue certificate
          </Button>
        )}
      </div>
    </header>
  );
}

function RecentIssuances({
  data,
  role,
}: {
  data: HomeData;
  role: Role;
}) {
  return (
    <div className="overflow-hidden rounded-[var(--radius)] border bg-card">
      <div className="flex items-center gap-3 border-b p-4 px-6">
        <h2 className="flex-1 text-xl font-semibold">
          {role === "USER" ? "Your organisation’s certificates" : "Recent issuances"}
        </h2>
        <Link href="/certificates" className="text-[13px] font-medium text-primary hover:underline">
          View all {data.totalCertificates.toLocaleString()}
        </Link>
      </div>
      <div className="w-full overflow-x-auto">
      <Table className="min-w-[720px] table-fixed">
        <TableHeader className="bg-muted">
          <TableRow className="hover:bg-transparent">
            <TableHead className="h-9 pl-6 text-[11px] font-semibold uppercase tracking-[0.1em]">
              Name
            </TableHead>
            <TableHead className="w-[220px] text-[11px] font-semibold uppercase tracking-[0.1em]">
              Competency
            </TableHead>
            <TableHead className="w-[120px] text-[11px] font-semibold uppercase tracking-[0.1em]">
              Issued
            </TableHead>
            <TableHead className="w-[130px] text-[11px] font-semibold uppercase tracking-[0.1em]">
              Status
            </TableHead>
            {role !== "USER" && (
              <TableHead className="w-24 pr-6 text-[11px] font-semibold uppercase tracking-[0.1em]">
                Email
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.recent.length === 0 && (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={role !== "USER" ? 5 : 4} className="p-0">
                <EmptyState
                  icon={Award}
                  title="No certificates yet"
                  description={
                    role === "USER"
                      ? "Nothing has been issued to your organisation yet."
                      : "Issue your first certificate and it will appear here."
                  }
                />
              </TableCell>
            </TableRow>
          )}
          {data.recent.map((r) => (
            <TableRow key={r.id} className="h-11 cursor-pointer">
              <TableCell className="truncate pl-6 text-sm font-medium">{r.name}</TableCell>
              <TableCell className="truncate text-[13px]">{r.competency}</TableCell>
              <TableCell className="whitespace-nowrap text-[13px]">{r.issuedAt}</TableCell>
              <TableCell>
                <StatusBadge status={r.status} />
              </TableCell>
              {role !== "USER" && (
                <TableCell
                  className={cn(
                    "pr-6 text-[13px]",
                    r.emailSent ? "text-foreground/80" : "text-muted-foreground/70"
                  )}
                >
                  {r.emailSent ? "Sent" : "Not sent"}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </div>
    </div>
  );
}

const ATTENTION_ICON = {
  expiring: Award,
  email: Mail,
  limit: CreditCard,
} as const;

/** Hidden entirely when there is nothing to act on. */
function NeedsAttention({ items }: { items: HomeData["attention"] }) {
  if (items.length === 0) return null;
  return (
    <div className="overflow-hidden rounded-[var(--radius)] border bg-card">
      <div className="flex items-center gap-2 border-b p-4 px-6">
        <h2 className="flex-1 text-xl font-semibold">Needs attention</h2>
        <Badge
          variant="outline"
          className="rounded-full border-accent-foreground/20 bg-accent px-2.5 py-0.5 text-xs font-semibold text-accent-foreground"
        >
          {items.length}
        </Badge>
      </div>
      {items.map((a) => {
        const Icon = ATTENTION_ICON[a.kind];
        return (
          <Link
            key={a.id}
            href={a.href}
            className="flex items-start gap-3 border-b p-6 py-4 transition-colors last:border-b-0 hover:bg-muted/60"
          >
            <span className="flex size-8 shrink-0 items-center justify-center rounded-[9px] bg-accent">
              <Icon className="size-[17px] text-accent-foreground" />
            </span>
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-sm font-medium text-pretty">{a.title}</span>
              <span className="text-xs text-muted-foreground text-pretty">{a.meta}</span>
              <span className="pt-0.5 text-[13px] font-medium text-primary">
                {a.ctaLabel} →
              </span>
            </span>
          </Link>
        );
      })}
    </div>
  );
}

const QUICK_LINKS = [
  { href: "/tenant/users", icon: Users, label: "Team" },
  { href: "/tenant/branding", icon: Palette, label: "Branding" },
  { href: "/tenant/email-templates", icon: Mail, label: "Email templates" },
];

function QuickLinks({
  tenantName,
  meta,
}: {
  tenantName: string;
  meta?: Record<string, string>;
}) {
  return (
    <Panel className="flex flex-col gap-4">
      <Eyebrow>Manage {tenantName}</Eyebrow>
      {QUICK_LINKS.map((q) => (
        <Link key={q.href} href={q.href} className="flex items-center gap-3 group">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-[9px] border bg-background">
            <q.icon className="size-[17px] text-muted-foreground" />
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="text-sm font-medium group-hover:underline">{q.label}</span>
            {meta?.[q.label] && (
              <span className="truncate text-xs text-muted-foreground">{meta[q.label]}</span>
            )}
          </span>
          <ChevronRight className="size-4 text-muted-foreground/60" />
        </Link>
      ))}
    </Panel>
  );
}

/** First run: no meters (nothing to meter), one clear action, a 3-step checklist. */
export function HomeFirstRun({
  data,
  steps,
  onIssue,
}: {
  data: HomeData;
  steps: { id: string; label: string; meta?: string; href?: string; done: boolean }[];
  onIssue?: () => void;
}) {
  const done = steps.filter((s) => s.done).length;
  return (
    <>
      <TenantHeader data={data} role="ADMIN" onIssue={onIssue} />

      <Panel className="flex flex-col items-center gap-3 py-12 text-center">
        <span className="flex size-12 items-center justify-center rounded-xl bg-accent">
          <Award className="size-5 text-accent-foreground" />
        </span>
        <p className="text-xl font-semibold">Issue your first certificate</p>
        <p className="max-w-[46ch] text-sm leading-relaxed text-muted-foreground text-pretty">
          Every certificate gets a public verification page and a scannable QR code the
          moment you issue it. Nothing is billed until you pass{" "}
          {data.meters.certificates.included}.
        </p>
        <div className="flex gap-2 pt-1">
          <Button className="gap-2" onClick={onIssue}>
            <Plus className="size-4" />
            Issue certificate
          </Button>
          <Button variant="outline" asChild>
            <Link href="/certificates?tab=create&mode=csv">Import from CSV</Link>
          </Button>
        </div>
      </Panel>

      <Panel className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <Eyebrow>Before you go live</Eyebrow>
          <div className="flex-1" />
          <span className="text-[13px] tabular-nums text-muted-foreground">
            {done} of {steps.length} done
          </span>
        </div>
        {steps.map((s) => (
          <div key={s.id} className="flex items-center gap-3">
            <span
              className={cn(
                "flex size-[22px] shrink-0 items-center justify-center rounded-full",
                s.done ? "bg-sidebar text-sidebar-foreground" : "border-[1.5px] border-muted-foreground/40"
              )}
            >
              {s.done && (
                <svg viewBox="0 0 24 24" className="size-3" fill="none" stroke="currentColor" strokeWidth={3.2} strokeLinecap="round">
                  <path d="m5 13 4 4 10-10" />
                </svg>
              )}
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className={cn("text-sm", s.done ? "text-muted-foreground line-through" : "font-medium")}>
                {s.label}
              </span>
              {s.meta && !s.done && (
                <span className="text-xs text-muted-foreground">{s.meta}</span>
              )}
            </span>
            {!s.done && s.href && (
              <Link href={s.href} className="text-[13px] font-medium text-primary hover:underline">
                {s.label.split(" ")[0]} →
              </Link>
            )}
          </div>
        ))}
      </Panel>
    </>
  );
}

export function HomePage({
  role,
  data,
  isLoading,
  onIssue,
}: {
  role: Role;
  data: HomeData;
  isLoading?: boolean;
  onIssue?: () => void;
}) {
  const isAdmin = role !== "USER";

  if (isLoading) {
    return (
      <>
        <StatTilesSkeleton count={2} />
        <div className="overflow-hidden rounded-[var(--radius)] border bg-card">
          <TableSkeleton rows={8} showCheckbox={false} numericColumns={2} />
        </div>
      </>
    );
  }

  return (
    <>
      <TenantHeader data={data} role={role} onIssue={onIssue} />

      {/* usage strip */}
      <Panel
        className={cn(
          "grid gap-6",
          isAdmin && data.cost.estimatedLabel
            ? "lg:grid-cols-[1fr_1fr_220px]"
            : "md:grid-cols-2"
        )}
      >
        <UsageMeter meter={data.meters.certificates} />
        <UsageMeter meter={data.meters.seats} />
        {isAdmin && data.cost.estimatedLabel ? (
          <div className="flex flex-col gap-1.5 border-t pt-4 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
            <Eyebrow>Estimated month</Eyebrow>
            <p className="text-[30px] font-semibold leading-none tabular-nums">
              {data.cost.estimatedLabel}
            </p>
            <p className="text-[13px] text-muted-foreground">{data.cost.breakdownLabel}</p>
            <div className="flex-1" />
            <Link
              href="/tenant/subscription"
              className="text-[13px] font-medium text-primary hover:underline"
            >
              Review plan &amp; billing
            </Link>
          </div>
        ) : null}
      </Panel>

      {isAdmin ? (
        <div className="grid items-start gap-6 lg:grid-cols-[1fr_380px]">
          <RecentIssuances data={data} role={role} />
          <div className="flex flex-col gap-6">
            <NeedsAttention items={data.attention} />
            <QuickLinks
              tenantName={data.tenant.name}
              meta={{
                Team: `${data.meters.seats.used} ${
                  data.meters.seats.used === 1 ? "member" : "members"
                }${
                  data.meters.seats.used > data.meters.seats.included
                    ? ` · ${data.meters.seats.used - data.meters.seats.included} seat over`
                    : ""
                }`,
                Branding: "Logo and colours on public pages",
                "Email templates": "Issuance, expiry and invitation emails",
              }}
            />
          </div>
        </div>
      ) : (
        <RecentIssuances data={data} role={role} />
      )}
    </>
  );
}
